// Agent Service - Helper para execução de agentes
import { API_CONFIG } from '../config/api';
import { getAccessToken } from '../utils/authStorage';

/** Padrões que indicam erro de rede (não erro do backend) */
export function isNetworkError(msg: string): boolean {
  return /failed to fetch|networkerror|load failed|network request failed|connection reset|aborted/i.test(msg || '');
}

/** Retorna mensagem amigável para exibição ao usuário */
export function getDisplayErrorMessage(msg: string): string {
  if (isNetworkError(msg)) return 'Problema de conexão. Verifique sua internet e tente novamente.';
  if (/timeout|demorou muito/i.test(msg || '')) return 'A requisição demorou muito. Tente novamente ou use uma entrada menor.';
  return msg || 'Erro ao executar o agente';
}

export interface ExecuteAgentResponse {
  status: string;
  agent: string;
  agent_code: string;
  text: string;
  tokens_used?: number;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  execution_time_ms?: number;
  from_cache?: boolean;
  error?: string;
  message?: string;
  details?: string;
}

/**
 * Executa um agente com os parâmetros fornecidos
 * 
 * @param agentCode - Código do agente (ex: 'devocional-pessoal', 'construir-sermao')
 * @param parameters - Parâmetros para o agente (ex: { prompt: '...', passagem: 'João 3:16' })
 * @returns Promise com a resposta do agente
 */
export async function executeAgent(
  agentCode: string,
  parameters: Record<string, any>
): Promise<ExecuteAgentResponse> {
  const token = getAccessToken();
  const url = `${API_CONFIG.BASE_URL}/api/agents/execute/${agentCode}/`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        parameters,
      }),
    });
  } catch (err: any) {
    throw new Error(getDisplayErrorMessage(err?.message || 'Erro de rede'));
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || errorData.details || `HTTP ${response.status}`);
  }

  const data: ExecuteAgentResponse = await response.json();

  if (!data.text && data.error) {
    throw new Error(data.error);
  }

  return data;
}

export interface StepProgress {
  id: string;
  title: string;
  status: 'pending' | 'running' | 'success' | 'error';
  error?: string;
}

export interface StreamEvent {
  type: 'steps' | 'step_start' | 'step_success' | 'step_error' | 'chunk' | 'done' | 'error' | 'disambiguation_required';
  steps?: StepProgress[];
  step_start?: { id: string; title: string };
  step_success?: { id: string; title: string };
  step_error?: { id: string; title: string; error: string };
  chunk?: string;
  done?: boolean;
  processed?: boolean;
  error?: string;
  isNetworkError?: boolean;
  creditsExhausted?: boolean;
  creditsInfo?: {
    usage_percent?: number;
    current_usage?: number;
    limit?: number;
    remaining?: number;
  } | null;
  disambiguation_required?: {
    options: string[];
    original_name: string;
  };
}

/**
 * Executa um agente com streaming e suporte a progresso de subagentes
 * 
 * @param agentCode - Código do agente
 * @param parameters - Parâmetros para o agente
 * @param callbacks - Callbacks para eventos de progresso
 * @returns AsyncGenerator que emite eventos de streaming
 */
export async function* executeAgentStream(
  agentCode: string,
  parameters: Record<string, any>,
  callbacks?: {
    onSteps?: (steps: StepProgress[]) => void;
    onStepStart?: (step: { id: string; title: string }) => void;
    onStepSuccess?: (step: { id: string; title: string }) => void;
    onStepError?: (step: { id: string; title: string; error: string }) => void;
    onChunk?: (chunk: string) => void;
    onDone?: (processed: boolean) => void;
    onError?: (error: string) => void;
    onDisambiguationRequired?: (data: { options: string[]; original_name: string }) => void;
  }
): AsyncGenerator<StreamEvent> {
  const token = getAccessToken();
  const url = `${API_CONFIG.BASE_URL}/api/agents/execute-stream/${agentCode}/`;
  const STREAM_TIMEOUT_MS = 900000; // 15 minutos
  const MAX_RETRIES = 2;
  const RETRY_DELAY_MS = 1500;

  let response: Response | null = null;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

      const fetchPromise = fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          parameters,
        }),
        signal: controller.signal,
      });

      response = await fetchPromise;
      clearTimeout(timeoutId);
      break;
    } catch (err: any) {
      lastError = err;
      const msg = err?.message || '';
      const isAbort = err?.name === 'AbortError';
      const isRetryable = isNetworkError(msg) || isAbort;

      if (isRetryable && attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        continue;
      }

      const displayMsg = isAbort
        ? 'A requisição demorou muito. Tente novamente ou use uma entrada menor.'
        : getDisplayErrorMessage(msg);
      if (callbacks?.onError) callbacks.onError(displayMsg);
      yield { type: 'error', error: displayMsg, isNetworkError: true };
      return;
    }
  }

  if (!response) {
    const displayMsg = getDisplayErrorMessage(lastError?.message || 'Erro de rede');
    if (callbacks?.onError) callbacks.onError(displayMsg);
    yield { type: 'error', error: displayMsg, isNetworkError: true };
    return;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = errorData.error || errorData.message || `HTTP ${response.status}`;
    
    // Detectar erro de créditos esgotados (403)
    if (response.status === 403 && (errorData.error === 'Créditos esgotados' || error.includes('Créditos esgotados') || errorData.message?.includes('Créditos esgotados'))) {
      // Não chamar onError aqui - será tratado no evento yield
      yield { 
        type: 'error', 
        error,
        creditsExhausted: true,
        creditsInfo: errorData.credits_info || null
      };
      return;
    }
    
    if (callbacks?.onError) callbacks.onError(error);
    yield { type: 'error', error };
    return;
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    const error = 'Não foi possível obter o stream de resposta';
    if (callbacks?.onError) callbacks.onError(error);
    yield { type: 'error', error };
    return;
  }

  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Processar buffer restante quando o stream terminar
        if (buffer.trim()) {
          const lines = buffer.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.slice(6).trim();
              if (!dataStr) continue;

              try {
                const data = JSON.parse(dataStr);

                // Processar eventos de progresso de subagentes
                if (data.steps) {
                  if (callbacks?.onSteps) callbacks.onSteps(data.steps);
                  yield { type: 'steps', steps: data.steps };
                }

                if (data.step_start) {
                  if (callbacks?.onStepStart) callbacks.onStepStart(data.step_start);
                  yield { type: 'step_start', step_start: data.step_start };
                }

                if (data.step_success) {
                  if (callbacks?.onStepSuccess) callbacks.onStepSuccess(data.step_success);
                  yield { type: 'step_success', step_success: data.step_success };
                }

                // Mapear step_complete para step_success (backend envia step_complete)
                if (data.step_complete) {
                  if (callbacks?.onStepSuccess) callbacks.onStepSuccess({
                    id: data.step_complete.id,
                    title: data.step_complete.title
                  });
                  yield { type: 'step_success', step_success: {
                    id: data.step_complete.id,
                    title: data.step_complete.title
                  }};
                }

                if (data.step_error) {
                  if (callbacks?.onStepError) callbacks.onStepError(data.step_error);
                  yield { type: 'step_error', step_error: data.step_error };
                }

                // Processar chunks de texto
                if (data.chunk) {
                  if (callbacks?.onChunk) callbacks.onChunk(data.chunk);
                  yield { type: 'chunk', chunk: data.chunk };
                }

                // Processar conclusão
                if (data.done !== undefined) {
                  if (callbacks?.onDone) callbacks.onDone(data.processed || false);
                  yield { type: 'done', done: true, processed: data.processed };
                }

                // Processar erros
                if (data.error) {
                  if (callbacks?.onError) callbacks.onError(data.error);
                  yield { type: 'error', error: data.error };
                }

                // Processar desambiguação
                if (data.disambiguation_required) {
                  if (callbacks?.onDisambiguationRequired) {
                    callbacks.onDisambiguationRequired(data.disambiguation_required);
                  }
                  yield { type: 'disambiguation_required', disambiguation_required: data.disambiguation_required };
                }
              } catch {
                // Ignorar erros de parsing de linhas parciais
              }
            }
          }
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr);

            // Processar eventos de progresso de subagentes
            if (data.steps) {
              if (callbacks?.onSteps) callbacks.onSteps(data.steps);
              yield { type: 'steps', steps: data.steps };
            }

            if (data.step_start) {
              if (callbacks?.onStepStart) callbacks.onStepStart(data.step_start);
              yield { type: 'step_start', step_start: data.step_start };
            }

            if (data.step_success) {
              if (callbacks?.onStepSuccess) callbacks.onStepSuccess(data.step_success);
              yield { type: 'step_success', step_success: data.step_success };
            }

            // Mapear step_complete para step_success (backend envia step_complete)
            if (data.step_complete) {
              if (callbacks?.onStepSuccess) callbacks.onStepSuccess({
                id: data.step_complete.id,
                title: data.step_complete.title
              });
              yield { type: 'step_success', step_success: {
                id: data.step_complete.id,
                title: data.step_complete.title
              }};
            }

            if (data.step_error) {
              if (callbacks?.onStepError) callbacks.onStepError(data.step_error);
              yield { type: 'step_error', step_error: data.step_error };
            }

            // Processar chunks de texto
            if (data.chunk) {
              if (callbacks?.onChunk) callbacks.onChunk(data.chunk);
              yield { type: 'chunk', chunk: data.chunk };
            }

            // Processar conclusão
            if (data.done !== undefined) {
              if (callbacks?.onDone) callbacks.onDone(data.processed || false);
              yield { type: 'done', done: true, processed: data.processed };
            }

                // Processar erros
                if (data.error) {
                  if (callbacks?.onError) callbacks.onError(data.error);
                  yield { type: 'error', error: data.error };
                }

                // Processar desambiguação
                if (data.disambiguation_required) {
                  if (callbacks?.onDisambiguationRequired) {
                    callbacks.onDisambiguationRequired(data.disambiguation_required);
                  }
                  yield { type: 'disambiguation_required', disambiguation_required: data.disambiguation_required };
                }
          } catch {
            // Ignorar erros de parsing de linhas parciais
          }
        }
      }
    }
  } catch (error: any) {
    const rawMsg = error?.message || 'Erro desconhecido no streaming';
    const errorMessage = getDisplayErrorMessage(rawMsg);
    const isNetErr = isNetworkError(rawMsg) || /timeout|demorou muito/i.test(rawMsg);
    if (callbacks?.onError) callbacks.onError(errorMessage);
    yield { type: 'error', error: errorMessage, isNetworkError: isNetErr };
  }
}

